import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { PromptIssue } from '@/lib/promptInlineAnalyzer';
import { Wrench, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

type InlinePromptEditorProps = {
  value: string;
  onChange: (value: string) => void;
  issues: PromptIssue[];
  onApplyFix: (issue: PromptIssue) => void;
  onAIFixSingle?: (issue: PromptIssue) => Promise<void>;
  onIgnoreIssue: (issueId: string) => void;
  onSelectIssue?: (issueId: string) => void;
  selectedIssueId?: string | null;
  disabled?: boolean;
};

const issueClassMap: Record<PromptIssue['type'], string> = {
  conflict: 'prompt-conflict',
  duplicate: 'prompt-duplicate',
  weak: 'prompt-warning',
  illogical: 'prompt-warning',
  valid: 'prompt-valid',
};

export default function InlinePromptEditor({
  value,
  onChange,
  issues,
  onApplyFix,
  onAIFixSingle,
  onIgnoreIssue,
  onSelectIssue,
  selectedIssueId,
  disabled = false,
}: InlinePromptEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fixingIssueId, setFixingIssueId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedIssueId || !overlayRef.current) return;
    const target = overlayRef.current.querySelector(`[data-issue-id="${selectedIssueId}"]`) as HTMLElement | null;
    target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectedIssueId]);

  const handleFixClick = useCallback(async (issue: PromptIssue, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fixingIssueId) return;

    if (onAIFixSingle) {
      setFixingIssueId(issue.id);
      try {
        await onAIFixSingle(issue);
      } finally {
        setFixingIssueId(null);
      }
    } else {
      onApplyFix(issue);
    }
  }, [fixingIssueId, onAIFixSingle, onApplyFix]);

  const decoratedParts = useMemo(() => {
    if (!issues.length) {
      return [<span key="valid-all" className="prompt-valid">{value || ' '}</span>];
    }

    const sorted = [...issues].sort((a, b) => a.start - b.start);
    const parts: JSX.Element[] = [];
    let cursor = 0;

    sorted.forEach((issue, idx) => {
      if (issue.start < cursor) return;

      if (issue.start > cursor) {
        const gap = value.slice(cursor, issue.start);
        if (gap.trim()) {
          parts.push(<span key={`valid-${idx}-${cursor}`} className="prompt-valid">{gap}</span>);
        } else {
          parts.push(<span key={`space-${idx}-${cursor}`}>{gap}</span>);
        }
      }

      const segment = value.slice(issue.start, issue.end);
      if (!segment) return;

      if (issue.type !== 'valid') {
        // Issue span with inline fix button
        parts.push(
          <span
            key={issue.id}
            data-issue-id={issue.id}
            className={`${issueClassMap[issue.type]} ${selectedIssueId === issue.id ? 'prompt-selected-issue' : ''} inline-issue-wrapper`}
            title={issue.message}
            onClick={(event) => {
              event.preventDefault();
              onSelectIssue?.(issue.id);
            }}
          >
            {segment}
            <button
              className="inline-fix-btn"
              onMouseDown={(e) => handleFixClick(issue, e)}
              title={`Fix: ${issue.message}`}
            >
              {fixingIssueId === issue.id ? (
                <Loader2 className="w-2.5 h-2.5 animate-spin" />
              ) : (
                <Wrench className="w-2.5 h-2.5" />
              )}
            </button>
          </span>
        );
      } else {
        parts.push(
          <span key={issue.id} className="prompt-valid">{segment}</span>
        );
      }

      cursor = issue.end;
    });

    if (cursor < value.length) {
      const tail = value.slice(cursor);
      if (tail.trim()) {
        parts.push(<span key={`valid-tail-${cursor}`} className="prompt-valid">{tail}</span>);
      } else {
        parts.push(<span key={`tail-${cursor}`}>{tail}</span>);
      }
    }

    return parts;
  }, [issues, onSelectIssue, selectedIssueId, value, handleFixClick, fixingIssueId]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative rounded-xl border border-border/30 bg-muted/10 overflow-hidden">
        {/* Textarea BELOW — captures keyboard input */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          spellCheck={false}
          className="absolute inset-0 resize-none border-0 bg-transparent p-3 font-mono text-[11px] leading-relaxed text-transparent caret-foreground focus:outline-none min-h-[100px] max-h-[200px] overflow-auto z-[1]"
          onScroll={(event) => {
            if (!overlayRef.current) return;
            overlayRef.current.scrollTop = event.currentTarget.scrollTop;
            overlayRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }}
        />
        {/* Overlay ON TOP — shows colored text + fix buttons */}
        <div
          ref={overlayRef}
          className="relative z-[2] whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-relaxed min-h-[100px] max-h-[200px] overflow-auto text-foreground/90 pointer-events-none"
        >
          <span className="prompt-overlay-layer">{decoratedParts}</span>
        </div>
      </div>
    </div>
  );
}